import { useCallback, useEffect, useRef, useState } from 'react';
import { useExitIntent } from 'use-exit-intent';

import useResponsive from './useResponsive';
import {
  hasInteractedThisSession,
  type ExitIntentPage,
} from '../utils/exitIntentStorage';

export type ExitIntentTriggerSource = 'mouse_leave' | 'idle';

interface UseExitIntentTriggerOptions {
  page: ExitIntentPage;
  applicantGuid: string;
  /** Delay applied AFTER mouse leaves the viewport before the modal fires. */
  mouseLeaveDelayMs: number;
  /** Seconds of inactivity before the idle trigger fires. */
  idleSeconds: number;
  /**
   * When true, the hook suppresses all triggers. Use to disable while the
   * candidate is recording / submitting, or once we've completed the flow.
   */
  suspended?: boolean;
}

interface UseExitIntentTriggerResult {
  isOpen: boolean;
  triggerSource: ExitIntentTriggerSource | null;
  dismiss: () => void;
  isMobile: boolean;
}

/**
 * VI-548 exit-intent trigger.
 *
 * Wraps `use-exit-intent` for idle detection (desktop + mobile) and adds a
 * custom `setTimeout`-based mouse-leave handler on desktop, per the ticket
 * note: "This package does not natively support a delay on mouse leave.
 * Please implement a custom delay of N seconds using `setTimeout` before
 * the modal fires."
 *
 * Behavior:
 *   - Once the candidate clicks any CTA on the current page (recorded via
 *     `markInteractedThisSession`), the hook stays suppressed for the rest
 *     of the tab session — no repeat pop-ups.
 *   - Mouse-leave timer is cleared if the cursor returns within the window.
 *   - On mobile, mouse-leave is never used (only idle).
 */
const useExitIntentTrigger = ({
  page,
  applicantGuid,
  mouseLeaveDelayMs,
  idleSeconds,
  suspended = false,
}: UseExitIntentTriggerOptions): UseExitIntentTriggerResult => {
  const isMobile = useResponsive('md');
  const [isOpen, setIsOpen] = useState(false);
  const [triggerSource, setTriggerSource] =
    useState<ExitIntentTriggerSource | null>(null);

  const suspendedRef = useRef(suspended);
  useEffect(() => {
    suspendedRef.current = suspended;
  }, [suspended]);

  const isBlocked = useCallback(
    () =>
      suspendedRef.current ||
      isOpen ||
      hasInteractedThisSession(applicantGuid, page),
    [isOpen, applicantGuid, page],
  );

  // VI-592 fix: idle must fire on BOTH desktop and mobile. We previously keyed
  // triggerOnIdle off `useResponsive` (isMobile), but that initializes to
  // false and `use-exit-intent` captures its settings once on init, so
  // mobile.triggerOnIdle stayed false and the mobile idle modal never fired.
  // The package itself chooses desktop vs mobile config at runtime, so we just
  // enable idle for both. Mouse-leave remains desktop-only via our own custom
  // handler below (gated by `isMobile`), so we keep the package's
  // triggerOnMouseLeave off.
  const { registerHandler, resetState } = useExitIntent({
    cookie: {
      key: `vi-548-exit-intent-${page}`,
      daysToExpire: 0,
    },
    desktop: {
      triggerOnIdle: true,
      triggerOnMouseLeave: false,
      useBeforeUnload: false,
      delayInSecondsToTrigger: idleSeconds,
    },
    mobile: {
      triggerOnIdle: true,
      delayInSecondsToTrigger: idleSeconds,
    },
  });

  // VI-624: when suspension lifts (q1→q2), reset the package's internal
  // fired state so the 120s idle countdown starts fresh. Without this,
  // any idle period on q1 (where suspended=true) "uses up" the package's
  // one-shot fire, leaving q2+ unable to trigger the modal.
  const prevSuspendedRef = useRef(suspended);
  useEffect(() => {
    if (prevSuspendedRef.current && !suspended) {
      resetState();
    }
    prevSuspendedRef.current = suspended;
  }, [suspended, resetState]);

  useEffect(() => {
    registerHandler({
      id: `vi-548-${page}-idle`,
      handler: () => {
        if (isBlocked()) return;
        setTriggerSource('idle');
        setIsOpen(true);
      },
    });
  }, [registerHandler, page, isBlocked]);

  useEffect(() => {
    if (isMobile) return;

    let timerId: number | null = null;

    const clearPendingTimer = () => {
      if (timerId !== null) {
        window.clearTimeout(timerId);
        timerId = null;
      }
    };

    const handleMouseLeave = () => {
      if (isBlocked()) return;
      if (timerId !== null) return;
      timerId = window.setTimeout(() => {
        timerId = null;
        if (isBlocked()) return;
        setTriggerSource('mouse_leave');
        setIsOpen(true);
      }, mouseLeaveDelayMs);
    };

    const handleMouseEnter = () => {
      clearPendingTimer();
    };

    document.documentElement.addEventListener('mouseleave', handleMouseLeave);
    document.documentElement.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      document.documentElement.removeEventListener('mouseleave', handleMouseLeave);
      document.documentElement.removeEventListener('mouseenter', handleMouseEnter);
      clearPendingTimer();
    };
  }, [isMobile, mouseLeaveDelayMs, isBlocked]);

  const dismiss = useCallback(() => {
    setIsOpen(false);
    setTriggerSource(null);
    resetState();
  }, [resetState]);

  return { isOpen, triggerSource, dismiss, isMobile };
};

export default useExitIntentTrigger;
